import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, of, Subject, ReplaySubject, timer } from 'rxjs';
import { map, tap, catchError, finalize, share, concatMap } from 'rxjs/operators';
import { ClientRecordsService } from './client-records.service';
import { OrganizationService } from './organization.service';
import { ProductService } from './product.service';
import { ContactResponseDto, OrganizationResponseDto, ProductResponseDto } from '@qc/shared';

interface ContactsCacheEntry {
    data: { items: ContactResponseDto[]; meta: any };
    searchTerm: string;
    timestamp: number;
    page: number;
    limit: number;
}

interface OrganizationContactsJob {
    requestFactory: () => Observable<any[]>;
    subject: ReplaySubject<any[]>;
}

interface OrganizationsCacheEntry {
    data: {
        items: OrganizationResponseDto[];
        meta: {
            totalCount: number;
            totalPages: number;
            currentPage: number;
            limit: number;
        };
    };
    searchTerm: string;
    timestamp: number;
    page: number;
    limit: number;
}

interface ProductsCacheEntry {
    data: {
        items: ProductResponseDto[];
        meta: {
            totalCount: number;
            totalPages: number;
            currentPage: number;
            limit: number;
        };
    };
    searchTerm: string;
    timestamp: number;
    page: number;
    limit: number;
}

type ContactsSearchResult = { items: ContactResponseDto[]; meta: any };
type OrganizationsSearchResult = {
    items: OrganizationResponseDto[];
    meta: {
        totalCount: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    };
};
type ProductsSearchResult = {
    items: ProductResponseDto[];
    meta: {
        totalCount: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    };
};

@Injectable({
    providedIn: 'root',
})
export class EntityCacheService {
    private clientRecordsService = inject(ClientRecordsService);
    private organizationService = inject(OrganizationService);
    private productService = inject(ProductService);
    private readonly CACHE_TTL = 5 * 60 * 1000;
    private contactsCache = new Map<string, ContactsCacheEntry>();
    private organizationsCache = new Map<string, OrganizationsCacheEntry>();
    private productsCache = new Map<string, ProductsCacheEntry>();
    private loadingContacts = new Map<string, Observable<ContactsSearchResult>>();
    private loadingOrganizations = new Map<string, Observable<OrganizationsSearchResult>>();
    private loadingProducts = new Map<string, Observable<ProductsSearchResult>>();
    private ongoingRequests = new Map<string, Observable<any>>();
    private genericCache = new Map<string, { data: any; timestamp: number; ttl: number }>();
    private currentContext: string = '';

    private readonly ORG_CONTACTS_THROTTLE_TTL_MS = 60_000;
    private readonly ORG_CONTACTS_THROTTLE_LIMIT = 6;
    private readonly ORG_CONTACTS_MIN_SPACING_MS = Math.ceil(this.ORG_CONTACTS_THROTTLE_TTL_MS / this.ORG_CONTACTS_THROTTLE_LIMIT);
    private orgContactsNextAvailableAt = 0;
    private organizationContactsQueue$ = new Subject<OrganizationContactsJob>();
    private organizationContactsQueueSub = this.organizationContactsQueue$
        .pipe(
            concatMap((job) =>
                timer(this.getOrgContactsQueueDelayMs()).pipe(
                    concatMap(() =>
                        job.requestFactory().pipe(
                            catchError(() => of([])),
                            tap((result) => job.subject.next(result)),
                            finalize(() => {
                                job.subject.complete();
                            }),
                        ),
                    ),
                ),
            ),
        )
        .subscribe();

    /**
     * Set the current context (e.g., CIR record ID, edit mode)
     * Cache will be cleared when context changes
     */
    setContext(context: string): void {
        if (this.currentContext !== context) {
            this.clearAllCache();
            this.currentContext = context;
        }
    }

    /**
     * Generic method to handle request deduplication
     * Prevents multiple concurrent requests with the same key
     */
    getOrCreateRequest<T>(key: string, requestFactory: () => Observable<T>): Observable<T> {
        if (this.ongoingRequests.has(key)) {
            return this.ongoingRequests.get(key)!;
        }

        const request$ = requestFactory().pipe(
            finalize(() => {
                this.ongoingRequests.delete(key);
            }),
            share(),
        );

        this.ongoingRequests.set(key, request$);
        return request$;
    }

    /**
     * Generic cached request method with automatic deduplication
     * Combines caching and request deduplication
     */
    getCachedRequest<T>(key: string, requestFactory: () => Observable<T>, ttl: number = this.CACHE_TTL): Observable<T> {
        const cached = this.genericCache.get(key);
        if (cached && Date.now() - cached.timestamp < cached.ttl) {
            return of(cached.data);
        }

        return this.getOrCreateRequest(key, requestFactory).pipe(
            tap((result) => {
                this.genericCache.set(key, {
                    data: result,
                    timestamp: Date.now(),
                    ttl,
                });
            }),
        );
    }

    /**
     * Get contacts with caching
     */
    getContacts(searchTerm: string = '', page: number = 1, limit: number = 100): Observable<ContactsSearchResult> {
        const cacheKey = this.getCacheKey(searchTerm, page, limit);

        if (this.loadingContacts.has(cacheKey)) {
            return this.loadingContacts.get(cacheKey)!;
        }

        const cached = this.contactsCache.get(cacheKey);
        if (cached && this.isCacheValidContacts(cached)) {
            return of(cached.data);
        }

        const request$ = this.clientRecordsService.searchContacts(searchTerm, page, limit).pipe(
            tap((result) => {
                this.contactsCache.set(cacheKey, {
                    data: result,
                    searchTerm,
                    timestamp: Date.now(),
                    page,
                    limit,
                });
            }),
            catchError((error) => {
                throw error;
            }),
            finalize(() => {
                this.loadingContacts.delete(cacheKey);
            }),
            share(),
        );

        this.loadingContacts.set(cacheKey, request$);
        return request$;
    }

    /**
     * Get organizations with caching
     */
    getOrganizations(
        searchTerm: string = '',
        page: number = 1,
        limit: number = 100,
    ): Observable<OrganizationsSearchResult> {
        const cacheKey = this.getCacheKey(searchTerm, page, limit);

        if (this.loadingOrganizations.has(cacheKey)) {
            return this.loadingOrganizations.get(cacheKey)!;
        }

        const cached = this.organizationsCache.get(cacheKey);
        if (cached && this.isCacheValidOrganizations(cached)) {
            return of(cached.data);
        }

        const request$ = this.organizationService.searchOrganizations(searchTerm, page, limit).pipe(
            tap((result) => {
                this.organizationsCache.set(cacheKey, {
                    data: result,
                    searchTerm,
                    timestamp: Date.now(),
                    page,
                    limit,
                });
            }),
            catchError((error) => {
                throw error;
            }),
            finalize(() => {
                this.loadingOrganizations.delete(cacheKey);
            }),
            share(),
        );

        this.loadingOrganizations.set(cacheKey, request$);
        return request$;
    }

    /**
     * Get products with caching
     */
    getProducts(searchTerm: string = '', page: number = 1, limit: number = 100): Observable<ProductsSearchResult> {
        const cacheKey = this.getCacheKey(searchTerm, page, limit);

        if (this.loadingProducts.has(cacheKey)) {
            return this.loadingProducts.get(cacheKey)!;
        }

        const cached = this.productsCache.get(cacheKey);
        if (cached && this.isCacheValidProducts(cached)) {
            return of(cached.data);
        }

        const request$ = this.productService.searchProducts(searchTerm, page, limit).pipe(
            tap((result) => {
                this.productsCache.set(cacheKey, {
                    data: result,
                    searchTerm,
                    timestamp: Date.now(),
                    page,
                    limit,
                });
            }),
            catchError((error) => {
                throw error;
            }),
            finalize(() => {
                this.loadingProducts.delete(cacheKey);
            }),
            share(),
        );

        this.loadingProducts.set(cacheKey, request$);
        return request$;
    }

    /**
     * Clear all cache
     */
    clearAllCache(): void {
        this.contactsCache.clear();
        this.organizationsCache.clear();
        this.productsCache.clear();
        this.loadingContacts.clear();
        this.loadingOrganizations.clear();
        this.loadingProducts.clear();
        this.ongoingRequests.clear();
        this.genericCache.clear();
        this.orgContactsNextAvailableAt = 0;
    }

    /**
     * Clear specific entity cache
     */
    clearContactsCache(): void {
        this.contactsCache.clear();
        this.loadingContacts.clear();
    }

    clearOrganizationsCache(): void {
        this.organizationsCache.clear();
        this.loadingOrganizations.clear();
    }

    /**
     * Invalidate cache when new entities are created/updated
     */
    invalidateContactsCache(): void {
        this.clearContactsCache();
    }

    invalidateOrganizationsCache(): void {
        this.clearOrganizationsCache();
    }

    /**
     * Invalidate specific cache entry
     */
    invalidateCacheEntry(key: string): void {
        this.genericCache.delete(key);
    }

    /**
     * Get contacts for a specific organization with caching
     * Contacts are now included in the organization response, so we fetch the organization
     * and extract the contacts from it.
     */
    getOrganizationContacts(organizationId: string): Observable<any[]> {
        const cacheKey = `organizationContacts:${organizationId}`;
        return this.getCachedRequest(
            cacheKey,
            () => this.enqueueOrganizationContactsRequest(organizationId),
            60 * 500,
        );
    }

    private enqueueOrganizationContactsRequest(organizationId: string): Observable<any[]> {
        const subject = new ReplaySubject<any[]>(1);
        this.organizationContactsQueue$.next({
            requestFactory: () =>
                this.clientRecordsService
                    .getContactsForOrganization(organizationId)
                    .pipe(map((contacts) => contacts || [])),
            subject,
        });
        return subject.asObservable();
    }

    private getOrgContactsQueueDelayMs(): number {
        const now = Date.now();
        const delay = Math.max(this.orgContactsNextAvailableAt - now, 0);
        this.orgContactsNextAvailableAt = Math.max(this.orgContactsNextAvailableAt, now) + this.ORG_CONTACTS_MIN_SPACING_MS;
        return delay;
    }

    /**
     * Invalidate organization contacts cache for a specific organization
     * Since contacts are now part of the organization response, we invalidate the organization cache
     */
    invalidateOrganizationContacts(organizationId: string): void {
        this.invalidateCacheEntry(`organizationContacts:${organizationId}`);
        this.organizationService.clearOrganizationCache(organizationId);
    }

    private getCacheKey(searchTerm: string, page: number, limit: number): string {
        return `${searchTerm}|${page}|${limit}`;
    }

    /**
     * Generate cache key for relation operations
     */
    getRelationCacheKey(parentType: string, parentId: string, relationType: string): string {
        return `${parentType}:${parentId}:${relationType}`;
    }

    private isCacheValidContacts(cacheEntry: ContactsCacheEntry): boolean {
        return Date.now() - cacheEntry.timestamp < this.CACHE_TTL;
    }

    private isCacheValidOrganizations(cacheEntry: OrganizationsCacheEntry): boolean {
        return Date.now() - cacheEntry.timestamp < this.CACHE_TTL;
    }

    private isCacheValidProducts(cacheEntry: ProductsCacheEntry): boolean {
        return Date.now() - cacheEntry.timestamp < this.CACHE_TTL;
    }
}
